import React, { useEffect, useCallback } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import Fieldset from "../form/Fieldset";
import { nonames, state_array, llcNameRegExp } from "../../variables/Variables";
import { usePrevious } from "../../V2/hooks";

export default function LegalInformation(props) {
  const prevProps = usePrevious(props);

  const validate = useCallback(
    (event) => {
      var inputElement = document.getElementById(event.target.id);
      var errorElement = inputElement.nextSibling;
      if (event.target.id === "legalName") {
        const notCountedCharacters = ["&", "-", " "];
        const notCountedWords = ["and", "or", "of", "the", "a"];
        const notAllowedEndings = ["corp", "corporation", "inc", "incorporated", "pa"];

        if (props.state.organizedState === "TN") {
          notAllowedEndings.push("usa");
        }

        if (props.state.organizedState === "IN") {
          notAllowedEndings.push("coop");
          notAllowedEndings.push("co-op");
          notAllowedEndings.push("cooperative");
          notAllowedEndings.push("coops");
          notAllowedEndings.push("co-ops");
          notAllowedEndings.push("cooperatives");
        }

        if (props.state.organizedState === "NJ") {
          notAllowedEndings.push("ltd");
        }

        if (event.target.value.trim().length === 0) {
          inputElement.classList.add("is-invalid");
          errorElement.innerHTML = "The legal name can't be empty";
          errorElement.style.display = "block";
        } else if (!/^[ a-zA-Z0-9&-]+$/.test(event.target.value)) {
          inputElement.classList.add("is-invalid");
          errorElement.innerHTML =
            "The only punctuation and special characters allowed are hyphen (-) and ampersand (&).";
          errorElement.style.display = "block";
        } else if (
          event.target.value
            .trim()
            .toLowerCase()
            .replace(/\s{2,}/g, " ")
            .split(" ")
            .some((fragment) => notAllowedEndings.includes(fragment))
        ) {
          inputElement.classList.add("is-invalid");

          if (props.state.organizedState === "TN") {
            errorElement.innerHTML =
              "The legal name may not contain any of the following endings: Corp, Corporation, Inc, Incorporated, PA, USA";
          } else if (props.state.organizedState === "IN") {
            errorElement.innerHTML =
              "The legal name may not contain any of the following endings: Corp, Corporation, Inc, Incorporated, PA, Coop, Co-op, Coops, Co-ops, Cooperative, Cooperatives";
          } else if (props.state.organizedState === "NJ") {
            errorElement.innerHTML =
              "The legal name may not contain any of the following endings: Corp, Corporation, Inc, Incorporated, PA, Ltd";
          } else {
            errorElement.innerHTML = "The legal name may not contain any of the following endings: Corp, Inc, PA";
          }

          errorElement.style.display = "block";
        } else if (
          props.state.organizedState === "NJ" &&
          event.target.value
            .trim()
            .toLowerCase()
            .replace(/\s{2,}/g, " ")
            .split(" ")
            .filter((fragment) => !notCountedWords.includes(fragment))
            .join("")
            .split("")
            .filter((character) => !notCountedCharacters.includes(character)).length < 4
        ) {
          inputElement.classList.add("is-invalid");
          errorElement.innerHTML =
            "The legal name must be at least 4 characters, excluding leading articles, spaces and conjunctions (AND, OR, OF, &)";
          errorElement.style.display = "block";
        } else {
          errorElement.style.display = "none";
          inputElement.classList.remove("is-invalid");
        }
      }
      if (event.target.id === "dbaname" && event.target.value) {
        if (event.target.value.trim().length > 0) {
          if (!/^[ a-zA-Z0-9&-]+$/.test(event.target.value)) {
            inputElement.classList.add("is-invalid");
            errorElement.innerHTML = "The Trade name/DBA could contain only letters, numbers and & - symbols";
            errorElement.style.display = "block";
          } else if (
            !/(?!.*(?:[ ][lL][cC]$))(?!.*(?:[ ][lL][lL][cC]$))(?!.*(?:[ ][pP][lL][lL][cC]$))(?!.*(?:[ ][pP][aA]$))(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][iI][nN][cC]$))^[ a-zA-Z0-9\\&-]+$/.test(
              event.target.value.trim()
            ) ||
            nonames.includes(event.target.value.trim().toLowerCase())
          ) {
            inputElement.classList.add("is-invalid");
            errorElement.innerHTML =
              "The Trade/DBA name may not contain an ending such as 'LLC', 'LC', 'PLLC', 'PA', 'Corp', or 'Inc'.";
            errorElement.style.display = "block";
          } else {
            errorElement.style.display = "none";
            inputElement.classList.remove("is-invalid");
          }
        } else {
          errorElement.style.display = "none";
          inputElement.classList.remove("is-invalid");
        }
      }
    },
    [props]
  );

  function handleChange(event) {
    validate(event);
    props.onChange(event);
  }

  useEffect(() => {
    if (prevProps?.state?.organizedState !== props.state.organizedState && props.state.legalName) {
      validate({ target: { id: "legalName", value: props.state.legalName || "" } });
    }
  }, [prevProps, props, validate]);

  return (
    <>
      <h5>Legal information</h5>
      <OverlayTrigger
        rootClose={true}
        placement="right-start"
        trigger="hover"
        overlay={
          <Tooltip id="overlay-example" className="helpTooltip">
            <strong>Legal Name of LLC</strong>
            <br />
            The only punctuation and special characters allowed are hyphen (-) and ampersand (&).
            <br />
            <br />
            The legal name may not contain any of the following endings: Corp, Inc, PA.
          </Tooltip>
        }
      >
        <Form.Group controlId="legalName">
          <Form.Label>Legal Name of the LLC</Form.Label>
          <Form.Control
            type="text"
            required
            placeholder="Name"
            pattern="(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][lL][cC]$))(?!.*(?:[ ][pP][lL][lL][cC]$))(?!.*(?:[ ][iI][nN][cC]$))^[ a-zA-Z0-9\\&-]+$"
            maxLength="70"
            onChange={handleChange}
            data-hj-allow
            value={props.state.legalName}
            defaultValue={props.state.legalName}
          />
          <Form.Control.Feedback type="invalid">Please provide a valid organization name</Form.Control.Feedback>
        </Form.Group>
      </OverlayTrigger>
      <OverlayTrigger
        rootClose={true}
        placement="right-start"
        trigger="hover"
        overlay={
          <Tooltip id="overlay-example" className="helpTooltip">
            <strong>Trade Name / Doing Business As</strong>
            <br />
            A trade name or doing business as name is another name under which the business or individual operates.
            These names are identified by the terms &apos;trading as&apos; (TA), &apos;doing business as&apos; (DBA), or
            &apos;also known as&apos; (AKA). To file for a DBA, a separate filing with your state and county must be
            completed. We will send you instructions on how to complete this process.
            <br />
            <br />
            The trade name may not contain an ending such as &apos;LLC&apos;, &apos;LC&apos;, &apos;PLLC&apos;,
            &apos;PA&apos;, &apos;Corp&apos;, or &apos;Inc&apos;. To the existing Help Box.
          </Tooltip>
        }
      >
        <Form.Group controlId="dbaname">
          <Form.Label>Trade name or DBA of the LLC</Form.Label>
          <Form.Control
            type="text"
            placeholder="Optional"
            pattern="(?!.*(?:[ ][lL][cC]$))(?!.*(?:[ ][lL][lL][cC]$))(?!.*(?:[ ][pP][lL][lL][cC]$))(?!.*(?:[ ][pP][aA]$))(?!.*(?:[ ][cC][oO][rR][pP]$))(?!.*(?:[ ][iI][nN][cC]$))^[ a-zA-Z0-9\\&-]+$"
            maxLength="35"
            onChange={handleChange}
            data-hj-allow
            defaultValue={props.state.dbaname}
          />
          <Form.Control.Feedback type="invalid">Please provide a valid trade name</Form.Control.Feedback>
        </Form.Group>
      </OverlayTrigger>
      <OverlayTrigger
        rootClose={true}
        placement="right-start"
        trigger="hover"
        overlay={
          <Tooltip id="overlay-example" className="helpTooltip">
            <strong>Number Of LLC Members</strong>
            <br />
            The owners of an LLC are called members and are similar in some respects to shareholders of a corporation. A
            member can be a natural person, a corporation, a partnership, or another legal association or entity.
            <br />
            <br />
            A single-member LLC is an LLC with only one member, who can be an individual or another organization. Since
            you are a single-member LLC, we must initially classify you as a disregarded entity. This means the LLC is
            ignored or &apos;disregarded&apos; for the purpose of filing a federal tax return. This is only a mechanism
            for tax purposes.
            <br />
            <br />A Multi-Member LLC must initially be classified as a partnership. If you do not wish to accept the
            default classification of partnership, you can: File Form 8832 (Entity Classification Election) to elect
            corporate status, or File Form 2553 (Election by a Small Business Corporation) to elect S corporation
            status.
          </Tooltip>
        }
      >
        <Form.Group controlId="LLCMembers">
          <Form.Label>Number of members in the LLC</Form.Label>
          <Form.Control
            required
            type="text"
            pattern="^(?:[1-9]\d{0,4}|100000)"
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
            onChange={handleChange}
            value={props.state.LLCMembers}
            data-hj-allow
          />
          <Form.Control.Feedback type="invalid">Please provide the number of LLC members</Form.Control.Feedback>
        </Form.Group>
      </OverlayTrigger>
      {props.state.LLCMembers === "2" ? (
        <div className="radio">
          <Fieldset
            id="husbandAndWife"
            label="Are the members husband and wife?"
            questionValue={props.state.husbandAndWife}
            onChange={handleChange}
          />
        </div>
      ) : null}
      {props.state.husbandAndWife === "true" ? (
        <fieldset>
          <Form.Group className="left-check" controlId="multiMember">
            <Form.Label>Please choose one of the following:</Form.Label>
            <Form.Check
              type="radio"
              label="We elect to be classified as a multi-member LLC."
              name="multiMember"
              id="multiMember1"
              checked={props.state.multiMember === "false"}
              value={"false"}
              onChange={handleChange}
            />
            <Form.Check
              type="radio"
              label="We elect to be classified as a single-member LLC."
              name="multiMember"
              id="multiMember2"
              checked={props.state.multiMember === "true"}
              value={"true"}
              onChange={handleChange}
            />
          </Form.Group>
        </fieldset>
      ) : null}
      <fieldset>
        <Form.Group className="left-check">
          <Form.Label>Select the Tax classification of the LLC:</Form.Label>
          <Form.Check
            type="radio"
            label="Individual/Partnership: Not taxed as a separate entity from owner(s)."
            name="taxClassification"
            id="taxClassification1"
            checked={props.state.taxClassification === "Individual"}
            value={"Individual"}
            onChange={handleChange}
          />
          <Form.Check
            type="radio"
            label="S-Corporation: Planning to elect an S-Corporation tax structure."
            name="taxClassification"
            id="taxClassification2"
            checked={props.state.taxClassification === "SCorp"}
            value={"SCorp"}
            onChange={handleChange}
          />
          <Form.Check
            type="radio"
            label="Corporation: LLC is planning to elect a Corporation tax structure."
            name="taxClassification"
            id="taxClassification3"
            checked={props.state.taxClassification === "Corporation"}
            value={"Corporation"}
            onChange={handleChange}
          />
        </Form.Group>
      </fieldset>
      <Form.Group controlId="organizedState">
        <Form.Label>In which state the LLC was incorporated?</Form.Label>
        <Form.Control
          required
          as="select"
          className="form-control"
          // id="organizedState"
          name="organizedState"
          value={props.state.organizedState}
          onChange={handleChange}
        >
          <option value="">Select a state</option>
          {Object.keys(state_array).map((prop, key) => {
            return (
              <option key={key} value={prop}>
                {state_array[prop]}
              </option>
            );
          })}
        </Form.Control>
        <Form.Control.Feedback type="invalid">
          Please provide the state where the company was organized
        </Form.Control.Feedback>
      </Form.Group>
    </>
  );
}
